import { useEffect, useState, useRef } from 'react';
import {
  Card,
  Stack,
  Input,
  Textarea,
  Button,
  Container,
  HStack,
  ColorPicker,
  Portal,
  parseColor,
  FileUpload,
  Icon,
  Flex,
  Box,
  Image,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { useParams } from 'react-router-dom';
import { PhotoIcon } from '@heroicons/react/24/outline';
import { toaster } from '@/components/ui/toaster';
import { Checkbox } from '@/components/ui/checkbox';
import { Field } from '@/components/ui/field';
import WorkspaceLayout from './workspace-layout';
import useWorkspace from '@/lib/hooks/use-workspace';
import { useAuth } from '@/lib/hooks/use-auth';
import useIsDefaultWorkspace from '@/lib/hooks/use-is-default-workspace';
import useWorkspaceStore from '@/utils/use-workspace';
import PartnerContent from './partner-content';
import api from '@/utils/api';

const formSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  set_default: z.boolean().optional(),
  is_public: z.boolean().optional(),
  primary_color: z.string().optional(),
  secondary_color: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

export default function EditAccount() {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const isDefaultWorkspace = useIsDefaultWorkspace();
  const { changeWorkspace, changeDefaultWorkspaceId } = useAuth();
  const { workspace } = useWorkspace();
  const { loadWorkspaces } = useWorkspaceStore();
  const [uploadedLogoUrl, setUploadedLogoUrl] = useState<string | null>(workspace?.logo || null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    control,
    handleSubmit,
    register,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
  });

  const onSubmit = async (data: FormData) => {
    try {
      const response = await api.put(`workspace/${workspaceId}`, {
        name: data.name,
        description: data.description,
        set_default: data.set_default,
        is_public: data.is_public,
        primary_color: data.primary_color,
        secondary_color: data.secondary_color,
        logo: uploadedLogoUrl,
      });

      const resData = response.data.data;
      const updatedWorkspace = {
        ...workspace,
        name: resData.name,
        description: resData.description,
        is_public: resData.is_public,
        is_base: resData.set_default,
        primary_color: resData.primary_color,
        secondary_color: resData.secondary_color,
      };
      changeWorkspace(updatedWorkspace);
      if (workspaceId && resData.set_default) {
        changeDefaultWorkspaceId(parseInt(workspaceId));
      }
      toaster.create({
        type: 'success',
        title: 'Workspace account updated successfully!',
      });
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to update workspace account',
      });
    }
  };

  const uploadFile = async (fileUpload: any) => {
    if (!fileUpload.files || fileUpload.files.length === 0) return;

    try {
      setIsUploading(true);

      const formData = new FormData();
      formData.append('logo', fileUpload.files[0]);

      const response = await api.post(`workspace/${workspaceId}/logo/upload`, formData);

      if (response.data?.data?.logo_path) {
        setUploadedLogoUrl(response.data.data.logo_path);

        const updatedWorkspace = {
          ...workspace,
          logo: response.data.data.logo_path,
        };
        changeWorkspace(updatedWorkspace);
        loadWorkspaces();
      }

      toaster.create({
        type: 'success',
        title: 'Workspace logo uploaded successfully!',
      });
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to upload workspace logo',
      });
    } finally {
      setIsUploading(false);
    }
  };

  useEffect(() => {
    if (workspace && isDefaultWorkspace !== undefined) {
      reset({
        name: workspace.name,
        description: workspace.description,
        set_default: isDefaultWorkspace,
        is_public: workspace.is_public,
        primary_color: workspace.primary_color || '#09090B',
        secondary_color: workspace.secondary_color || '#f7f5f2',
      });
      setUploadedLogoUrl(workspace.logo || null);
    }
  }, [workspace, isDefaultWorkspace, reset]);

  return (
    <WorkspaceLayout>
      <Card.Root minH={'100vh'} mx={'auto'} width={{ md: '90%' }} maxW={'6xl'}>
        <Card.Header>
          <Card.Title fontSize='3xl' textAlign='center' mb={4}>
            Update workspace
          </Card.Title>
        </Card.Header>
        <Card.Body>
          <Container maxW={'3xl'}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Stack direction='column' gap={6}>
                <Field label='Name' invalid={!!errors.name} errorText={errors.name?.message}>
                  <Input {...register('name')} />
                </Field>
                <Field label='Description'>
                  <Textarea rows={6} {...register('description')} />
                </Field>
                <Field label='Workspace Logo'>
                  <Stack gap={4}>
                    {uploadedLogoUrl && (
                      <Flex align='center' gap={3}>
                        <Box
                          width='100px'
                          height='100px'
                          borderRadius='md'
                          overflow='hidden'
                          border='1px solid'
                          borderColor='gray.200'
                        >
                          <Image
                            src={uploadedLogoUrl}
                            alt='Workspace Logo'
                            width='100%'
                            height='100%'
                            objectFit='cover'
                          />
                        </Box>
                        <Stack gap={1}>
                          <Box fontSize='sm' color='gray.600'>
                            Current logo
                          </Box>
                          <Button
                            size='sm'
                            variant='outline'
                            loading={isUploading}
                            loadingText='Uploading...'
                            disabled={isUploading}
                            onClick={() => fileInputRef.current?.click()}
                          >
                            Change Logo
                          </Button>
                        </Stack>
                      </Flex>
                    )}

                    <FileUpload.Root
                      accept={['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif']}
                      maxFileSize={5 * 1024 * 1024} // 5MB max size
                      disabled={isUploading}
                      onFileAccept={(file) => {
                        if (file) {
                          uploadFile(file);
                        }
                      }}
                    >
                      <FileUpload.HiddenInput ref={fileInputRef} />

                      {!uploadedLogoUrl && (
                        <FileUpload.Trigger asChild>
                          <Button
                            variant='outline'
                            size='sm'
                            mt={2}
                            loading={isUploading}
                            loadingText='Uploading...'
                            disabled={isUploading}
                          >
                            <Icon as={PhotoIcon} h={4} w={4} />
                            Select file
                          </Button>
                        </FileUpload.Trigger>
                      )}
                    </FileUpload.Root>
                  </Stack>
                </Field>
                <Field label='Button Colour'>
                  <Controller
                    name='primary_color'
                    control={control}
                    render={({ field }) => (
                      <ColorPicker.Root
                        name={field.name}
                        value={parseColor(field.value || '#09090B')}
                        onValueChange={(e) => field.onChange(e.value.toString('hex'))}
                      >
                        <ColorPicker.HiddenInput />
                        <ColorPicker.Control>
                          <ColorPicker.Input />
                          <ColorPicker.Trigger />
                        </ColorPicker.Control>
                        <Portal>
                          <ColorPicker.Positioner>
                            <ColorPicker.Content>
                              <ColorPicker.Area />
                              <HStack>
                                <ColorPicker.EyeDropper size='sm' variant='outline' />
                                <ColorPicker.Sliders />
                              </HStack>
                            </ColorPicker.Content>
                          </ColorPicker.Positioner>
                        </Portal>
                      </ColorPicker.Root>
                    )}
                  />
                </Field>
                <Field label='Background Colour'>
                  <Controller
                    name='secondary_color'
                    control={control}
                    render={({ field }) => (
                      <ColorPicker.Root
                        name={field.name}
                        value={parseColor(field.value || '#f7f5f2')}
                        onValueChange={(e) => field.onChange(e.value.toString('hex'))}
                      >
                        <ColorPicker.HiddenInput />
                        <ColorPicker.Control>
                          <ColorPicker.Input />
                          <ColorPicker.Trigger />
                        </ColorPicker.Control>
                        <Portal>
                          <ColorPicker.Positioner>
                            <ColorPicker.Content>
                              <ColorPicker.Area />
                              <HStack>
                                <ColorPicker.EyeDropper size='sm' variant='outline' />
                                <ColorPicker.Sliders />
                              </HStack>
                            </ColorPicker.Content>
                          </ColorPicker.Positioner>
                        </Portal>
                      </ColorPicker.Root>
                    )}
                  />
                </Field>
                <Controller
                  control={control}
                  name='set_default'
                  render={({ field }) => (
                    <Field disabled={isDefaultWorkspace}>
                      <Checkbox
                        colorPalette='brand'
                        checked={field.value}
                        onCheckedChange={({ checked }) => field.onChange(checked)}
                      >
                        Set a default workspace
                      </Checkbox>
                    </Field>
                  )}
                />
                <Controller
                  control={control}
                  name='is_public'
                  render={({ field }) => (
                    <Field>
                      <Checkbox
                        colorPalette='brand'
                        checked={field.value}
                        onCheckedChange={({ checked }) => field.onChange(checked)}
                      >
                        Make public
                      </Checkbox>
                    </Field>
                  )}
                />
                <PartnerContent />
                <Button
                  colorPalette='brand'
                  type={'submit'}
                  width={'full'}
                  loading={isSubmitting}
                  loadingText='Loading...'
                >
                  Save
                </Button>
              </Stack>
            </form>
          </Container>
        </Card.Body>
      </Card.Root>
    </WorkspaceLayout>
  );
}
